/** Food labels are a human-reviewed aid, never a certification or an exhaustive allergy list. */
export const FOOD_TAG_IDS = ['MILK', 'EGG', 'FISH', 'CRUSTACEAN', 'TREE_NUT', 'PEANUT', 'WHEAT', 'SOY', 'SESAME', 'GLUTEN', 'SPICY'] as const;
export type FoodTagId = typeof FOOD_TAG_IDS[number];
export interface SupplyFoodLabels {
  tags: FoodTagId[];
  reviewed: boolean;
  /** Optional manufacturer ingredients/contains statement, not inferred from a generated photo. */
  labelText: string;
  source: 'MANUAL' | 'AI' | 'DEMO';
  reviewedAt?: string;
  reviewedByUid?: string;
}
export interface FoodTagSummary { tags: FoodTagId[]; needsReview: boolean }
export const FOOD_TAGS: ReadonlyArray<{ id: FoodTagId; label: { en: string; es: string }; kind: 'ALLERGEN' | 'GLUTEN' | 'ATTRIBUTE' }> = [
  { id: 'MILK', label: { en: 'Milk / dairy', es: 'Leche / lácteos' }, kind: 'ALLERGEN' },
  { id: 'EGG', label: { en: 'Egg', es: 'Huevo' }, kind: 'ALLERGEN' },
  { id: 'FISH', label: { en: 'Fish', es: 'Pescado' }, kind: 'ALLERGEN' },
  { id: 'CRUSTACEAN', label: { en: 'Crustaceans (shrimp, crab)', es: 'Crustáceos (camarón, cangrejo)' }, kind: 'ALLERGEN' },
  { id: 'TREE_NUT', label: { en: 'Tree nuts', es: 'Frutos secos de árbol' }, kind: 'ALLERGEN' },
  { id: 'PEANUT', label: { en: 'Peanut', es: 'Maní / cacahuate' }, kind: 'ALLERGEN' },
  { id: 'WHEAT', label: { en: 'Wheat', es: 'Trigo' }, kind: 'ALLERGEN' },
  { id: 'SOY', label: { en: 'Soy', es: 'Soya' }, kind: 'ALLERGEN' },
  { id: 'SESAME', label: { en: 'Sesame', es: 'Sésamo / ajonjolí' }, kind: 'ALLERGEN' },
  { id: 'GLUTEN', label: { en: 'Gluten', es: 'Gluten' }, kind: 'GLUTEN' },
  { id: 'SPICY', label: { en: 'Spicy', es: 'Picante' }, kind: 'ATTRIBUTE' },
];
export function emptyFoodLabels(): SupplyFoodLabels { return { tags: [], reviewed: false, labelText: '', source: 'MANUAL' }; }
export function isFoodTagId(value: unknown): value is FoodTagId { return typeof value === 'string' && (FOOD_TAG_IDS as readonly string[]).includes(value); }
/** Display helper only. Backend must reject malformed input rather than silently filtering it. */
export function normalizeFoodTagIds(value: unknown): FoodTagId[] {
  if (!Array.isArray(value)) return [];
  return FOOD_TAG_IDS.filter(id => value.includes(id));
}
export function foodTagLabel(id: FoodTagId, locale: 'en' | 'es'): string { return FOOD_TAGS.find(tag => tag.id === id)!.label[locale]; }

/** Live union, including pending suggestions. Missing/unreviewed inputs must never look verified. */
export function aggregateRecipeFoodTags(
  recipe: { ingredients: ReadonlyArray<{ itemId: string }> },
  items: ReadonlyArray<{ id: string; foodLabels?: SupplyFoodLabels }>,
): FoodTagSummary {
  const byId = new Map(items.map(item => [item.id, item]));
  const all: FoodTagId[] = [];
  let needsReview = recipe.ingredients.length === 0;
  for (const ingredient of recipe.ingredients) {
    const labels = byId.get(ingredient.itemId)?.foodLabels;
    if (!labels || labels.reviewed !== true || !Array.isArray(labels.tags) || labels.tags.some(tag => !isFoodTagId(tag))) needsReview = true;
    all.push(...normalizeFoodTagIds(labels?.tags));
  }
  return { tags: normalizeFoodTagIds(all), needsReview };
}

export const ALLERGY_NOTICES = {
  NONE: { en: '', es: '' },
  ASK_STAFF: {
    en: 'Have a food allergy? Please tell our staff before ordering and ask about ingredients. Icons are not an exhaustive list of allergens.',
    es: '¿Tienes una alergia alimentaria? Informa al personal antes de ordenar y consulta los ingredientes. Los iconos no son una lista exhaustiva de alérgenos.',
  },
  CROSS_CONTACT: {
    en: 'Please ask our staff about ingredients and possible allergen cross-contact from shared preparation areas or equipment before ordering.',
    es: 'Antes de ordenar, consulta al personal sobre los ingredientes y el posible contacto cruzado con alérgenos en áreas de preparación o equipos compartidos.',
  },
} as const;
export type AllergyNoticeId = keyof typeof ALLERGY_NOTICES;
export const FOOD_TAG_CAUTION = {
  en: 'Verify allergens with our staff. The absence of an icon does not mean a dish is allergen-free.',
  es: 'Verifica los alérgenos con nuestro personal. La ausencia de un icono no significa que el plato esté libre de alérgenos.',
};

/** Demo-only lexical simulation; never call it AI, verified, or a negative/allergen-free result. */
export function demoSuggestFoodTags(name: { en: string; es: string }, labelText = ''): FoodTagId[] {
  const text = `${name.en} ${name.es} ${labelText}`.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const rules: [FoodTagId, RegExp][] = [
    ['MILK', /\b(milk|dairy|cheese|butter|whey|casein|leche|lacteos|queso|mantequilla|yogurt|yogur)\b/],
    ['EGG', /\b(egg|eggs|huevo|huevos|mayonnaise|mayonesa)\b/],
    ['FISH', /\b(fish|salmon|tuna|pescado|atun|tilapia|anchovy|anchoa)\b/],
    ['CRUSTACEAN', /\b(shrimp|prawn|crab|lobster|camaron|camarones|cangrejo|langosta)\b/],
    ['TREE_NUT', /\b(almond|almonds|walnut|walnuts|pecan|pecans|cashew|cashews|hazelnut|pistachio|almendra|almendras|nuez|nueces|avellana|pistacho|anacardo)\b/],
    ['PEANUT', /\b(peanut|peanuts|mani|cacahuate|cacahuetes)\b/],
    ['WHEAT', /\b(wheat|trigo)\b/],
    ['SOY', /\b(soy|soybean|soybeans|soya|soja|tofu)\b/],
    ['SESAME', /\b(sesame|sesamo|ajonjoli|tahini)\b/],
    ['GLUTEN', /\b(gluten|wheat|trigo|barley|cebada|rye|centeno)\b/],
    ['SPICY', /\b(spicy|chili|chilli|jalapeno|habanero|picante|cayenne|cayena)\b/],
  ];
  // Even matching negative phrases remains a pending suggestion, never an assertion.
  return normalizeFoodTagIds(rules.filter(([, pattern]) => pattern.test(text)).map(([tag]) => tag));
}
